import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Binary theme model.
 *
 *   * `theme: null`        — never been touched. Renders LIGHT.
 *   * `theme: "light" | "dark"` — user made an explicit choice, which wins.
 *
 * Why no `"system"` value?  A three-way toggle (light → dark → system)
 * requires two clicks to flip light↔dark, and "system" reads as a noisy
 * third state in the UI. With a binary toggle and a hidden default,
 * one click always flips between light and dark — which is what people
 * actually want.
 *
 * Why light rather than following the OS (2026-08-04)?  Control-room and
 * kiosk screens are shared, and an operator's personal OS setting was
 * deciding what everyone in the room saw — two identical stations could
 * disagree. Light is the documented default for the product; dark stays
 * one click away and, once chosen, persists. The NOC kiosk keeps its own
 * separate preference because ambient light there is a property of the
 * site, not of the person.
 */

export type Theme = "light" | "dark";

/** What `theme: null` renders as. Changing this changes the product default. */
export const DEFAULT_THEME: Theme = "light";

interface ThemeState {
  /** null = product default (light); "light"|"dark" = explicit user choice. */
  theme: Theme | null;
  setTheme: (t: Theme | null) => void;
  /** Resolve the *effective* theme right now (used by the toggle). */
  resolved: () => Theme;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: null,
      setTheme: (theme) => set({ theme }),
      resolved: () => get().theme ?? DEFAULT_THEME,
      toggle: () => {
        const next: Theme = get().resolved() === "dark" ? "light" : "dark";
        set({ theme: next });
      },
    }),
    { name: "expertgas.theme.v3" },
  ),
);

/** Mount once near the app root; mirrors the chosen theme onto <html>. */
export function useBindTheme() {
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    const root = document.documentElement;
    const resolved: Theme = theme ?? DEFAULT_THEME;
    root.dataset.theme = resolved;
    root.style.colorScheme = resolved;
    // No prefers-color-scheme listener: the default is the product default,
    // not the operator's OS. See the note on DEFAULT_THEME.
  }, [theme]);
}
